//TODO : Refactor after ai
/**
 * Detailseite eines Datensatzes – für jede Objektklasse gleich aufgebaut:
 * Kopf mit Kennung, Titel, wichtigsten Angaben, Statusleiste und den Actions,
 * die der gewählten Rolle im aktuellen Zustand zustehen; links die
 * Mengenbeziehungen (x:n) mit „anlegen“ für Positionen und Zeilen; rechts die
 * Verweise (x:1) und alle Angaben.
 */

import { useState } from "react";
import { Plus, Smartphone } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { personaVon, useDatenStore } from "@app/store";
import {
  Attribute,
  AttributeTable,
  Badge,
  Button,
  ObjectDetailLayout,
  ObjectHeader,
  ObjectListCard,
  ObjectRow,
  StatusBand,
} from "@shared/components/widgets/ooux";
import {
  anlegeAktion,
  ausgehendeRelationen,
  bezogene,
  datensatz,
  eingehendeRelationen,
  erlaubteAktionen,
  folgeAktionen,
  formatWert,
  objektKlasse,
  relationsTitel,
  statusFarbe,
  verweisende,
  type Aktion,
  type Datensatz,
  type ObjektKlasse,
  type Relation,
} from "@shared/demo-data/viacid";
import { useTranslation } from "@shared/locales";
import { BeziehungDialog } from "./BeziehungDialog";
import { SatzFormular } from "./SatzFormular";
import { bedienungVon, useStatusAktion } from "./aktionen";
import { angaben } from "./uebersichtsObjekt";

interface Sammlung {
  relation: Relation;
  richtung: "eingehend" | "ausgehend";
  titel: string;
  klasse: ObjektKlasse;
  saetze: Datensatz[];
}

/** Summe netto unter Positionslisten (Menge × Einzelpreis). */
function summeVon(saetze: Datensatz[]) {
  const mitPreis = saetze.filter(
    (d) => typeof d.werte.einzelpreis === "number"
  );
  if (mitPreis.length === 0) return undefined;
  const summe = mitPreis.reduce(
    (s, d) => s + Number(d.werte.menge ?? 0) * Number(d.werte.einzelpreis),
    0
  );
  return (
    <span
      style={{ font: "var(--t-paragraph-s)", color: "var(--text-secondary)" }}
    >
      Summe netto Fr.{" "}
      {summe.toLocaleString("de-CH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </span>
  );
}

type Offen =
  | {
      art: "formular";
      klasse: ObjektKlasse;
      aktion: Aktion;
      satz?: Datensatz;
      vorbelegung?: Record<string, string>;
    }
  | { art: "beziehung"; aktion: Aktion };

export function ObjektDetailPage() {
  const { slug = "", id = "" } = useParams<{ slug: string; id: string }>();
  const { t } = useTranslation();
  const bestand = useDatenStore((s) => s.bestand);
  const klasse = objektKlasse(slug);
  const satz = klasse ? datensatz(bestand, klasse.slug, id) : undefined;

  if (!klasse || !satz) {
    return (
      <p style={{ color: "var(--text-tertiary)" }}>
        {t("viacid.unbekannterDatensatz")}
      </p>
    );
  }
  return (
    <Detail key={`${klasse.slug}-${satz.id}`} klasse={klasse} satz={satz} />
  );
}

function Detail({ klasse, satz }: { klasse: ObjektKlasse; satz: Datensatz }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const anwenden = useDatenStore((s) => s.anwenden);
  const statusAktion = useStatusAktion();
  const [offen, setOffen] = useState<Offen | null>(null);

  const oeffne = (ziel: string, zielId: string) =>
    navigate(PagesRoutes.objekte.show(ziel, zielId));

  const sammlungen: Sammlung[] = [
    ...eingehendeRelationen(klasse.slug).map((r) => ({
      relation: r,
      richtung: "eingehend" as const,
      titel: relationsTitel(r, "eingehend"),
      klasse: objektKlasse(r.von) as ObjektKlasse,
      saetze: verweisende(bestand, r, satz.id),
    })),
    ...ausgehendeRelationen(klasse.slug)
      .filter((r) => r.zuViele)
      .map((r) => ({
        relation: r,
        richtung: "ausgehend" as const,
        titel: relationsTitel(r, "ausgehend"),
        klasse: objektKlasse(r.zu) as ObjektKlasse,
        saetze: bezogene(bestand, r, satz),
      })),
  ]
    .filter(
      (s) =>
        s.saetze.length > 0 ||
        (s.richtung === "eingehend" && !s.klasse.inNavigation)
    )
    .sort(
      (a, b) =>
        Number(a.klasse.inNavigation) - Number(b.klasse.inNavigation) ||
        a.klasse.prioritaet - b.klasse.prioritaet
    );

  const verweise = ausgehendeRelationen(klasse.slug)
    .filter((r) => !r.zuViele)
    .map((r) => {
      const ziel = bezogene(bestand, r, satz)[0];
      return {
        label: relationsTitel(r, "ausgehend"),
        value: ziel ? (
          <button
            type="button"
            onClick={() => oeffne(r.zu, ziel.id)}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              cursor: "pointer",
              textAlign: "left",
              color: "var(--brand-600, #444ce7)",
              font: "inherit",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            {ziel.kennung ? `${ziel.kennung} · ${ziel.titel}` : ziel.titel}
          </button>
        ) : (
          "—"
        ),
      };
    });

  // Zustandswechsel zuerst – sie bringen den Vorgang weiter; Bearbeiten und Zuordnen danach
  const aktionen = [...erlaubteAktionen(klasse, satz, rolle)].sort(
    (a, b) =>
      Number(a.ziel == null) - Number(b.ziel == null) ||
      a.prioritaet - b.prioritaet
  );
  const folge = folgeAktionen(bestand, klasse.slug, satz, rolle);
  const keineRechte =
    klasse.actions.length > 0 && aktionen.length === 0 && folge.length === 0;

  const ausfuehren = (a: Aktion) => {
    const bedienung = bedienungVon(a);
    if (bedienung === "formular")
      setOffen({ art: "formular", klasse, aktion: a, satz });
    else if (bedienung === "beziehung")
      setOffen({ art: "beziehung", aktion: a });
    else statusAktion(klasse, satz, a);
  };

  const kopfAktionen = (
    <>
      {klasse.mobil && (
        <Badge color="violet" icon={<Smartphone size={14} />}>
          {t("viacid.mobil")}
        </Badge>
      )}
      {folge.map((f) => (
        <Button
          key={f.key}
          size="sm"
          title={t("viacid.notionAction", { aktion: f.aktion })}
          onClick={() => {
            const ergebnis = f.ausfuehren(bestand, satz, personaVon(rolle));
            anwenden(ergebnis);
            if (ergebnis.oeffnen && !ergebnis.fehler)
              oeffne(ergebnis.oeffnen.slug, ergebnis.oeffnen.id);
          }}
        >
          {f.label}
        </Button>
      ))}
      {aktionen.map((a, i) => (
        <Button
          key={a.name}
          size="sm"
          variant={folge.length === 0 && i === 0 ? "filled" : "outline"}
          color={folge.length === 0 && i === 0 ? "brand" : "neutral"}
          onClick={() => ausfuehren(a)}
        >
          {a.name}
        </Button>
      ))}
      {keineRechte && (
        <span
          style={{
            font: "var(--t-paragraph-s)",
            color: "var(--text-tertiary)",
          }}
        >
          {t("viacid.keineActions")}
        </span>
      )}
    </>
  );

  return (
    <>
      <ObjectDetailLayout
        header={
          <ObjectHeader
            type={klasse.name}
            identifier={satz.kennung ?? undefined}
            title={satz.titel}
            subtitle={satz.untertitel ?? undefined}
            actions={kopfAktionen}
            attributes={angaben(klasse, satz, 4).map((a) => (
              <Attribute key={a.label} label={a.label} value={a.value} />
            ))}
            statusBand={
              klasse.zustaende.length > 0 ? (
                <StatusBand
                  steps={klasse.zustaende.map((z) => ({ label: z }))}
                  current={satz.status ?? undefined}
                  interactive={false}
                />
              ) : undefined
            }
          />
        }
        relations={
          sammlungen.length > 0 ? (
            sammlungen.map((s) => {
              const kindAnlegen =
                s.richtung === "eingehend" && !s.relation.zuViele
                  ? anlegeAktion(s.klasse, rolle)
                  : undefined;
              return (
                <ObjectListCard
                  key={`${s.richtung}-${s.relation.id}`}
                  title={s.titel}
                  count={s.saetze.length}
                  empty={t("viacid.keineEintraege")}
                  footer={summeVon(s.saetze)}
                  actions={
                    kindAnlegen ? (
                      <Button
                        size="xs"
                        variant="light"
                        leadingIcon={<Plus size={14} />}
                        onClick={() =>
                          setOffen({
                            art: "formular",
                            klasse: s.klasse,
                            aktion: kindAnlegen,
                            vorbelegung: { [s.relation.id]: satz.id },
                          })
                        }
                      >
                        {kindAnlegen.name}
                      </Button>
                    ) : undefined
                  }
                >
                  {s.saetze.map((d) => (
                    <ObjectRow
                      key={d.id}
                      title={d.titel}
                      meta={[d.kennung, d.untertitel]
                        .filter(Boolean)
                        .join(" · ")}
                      status={
                        d.status ? (
                          <Badge color={statusFarbe(s.klasse, d.status)} dot>
                            {d.status}
                          </Badge>
                        ) : undefined
                      }
                      onClick={() => oeffne(s.klasse.slug, d.id)}
                    />
                  ))}
                </ObjectListCard>
              );
            })
          ) : (
            <p style={{ color: "var(--text-tertiary)" }}>
              {t("viacid.keineBeziehungen")}
            </p>
          )
        }
        aside={
          <>
            {verweise.length > 0 && (
              <AttributeTable label={t("viacid.verweise")} items={verweise} />
            )}
            <AttributeTable
              label={t("viacid.angaben")}
              items={klasse.felder.map((f) => ({
                label: f.label,
                value: formatWert(f, satz.werte[f.key]),
              }))}
            />
          </>
        }
      />
      {offen?.art === "formular" && (
        <SatzFormular
          klasse={offen.klasse}
          aktion={offen.aktion}
          satz={offen.satz}
          vorbelegung={offen.vorbelegung}
          onClose={() => setOffen(null)}
        />
      )}
      {offen?.art === "beziehung" && (
        <BeziehungDialog
          klasse={klasse}
          aktion={offen.aktion}
          satz={satz}
          onClose={() => setOffen(null)}
        />
      )}
    </>
  );
}
